import React, { useCallback } from "react";
import { AnalyticsBrowser } from "@segment/analytics-next";
import {
  OneTrustCookieMissingReporter,
  type OneTrustCookieMissingEventProperties,
} from "./OneTrustCookieMissingReporter";

type TrackWithWriteKey = (
  writeKey: string,
  eventName: "Error Reported",
  properties: OneTrustCookieMissingEventProperties,
) => Promise<unknown> | void;

type PublicOneTrustCookieMissingReporterProps = {
  enabled: boolean;
  surface: "MP" | "MOH";
  writeKey: string;
  trackWithWriteKey?: TrackWithWriteKey;
};

let publicAnalytics: AnalyticsBrowser | null = null;
let publicAnalyticsWriteKey: string | null = null;

const getPublicAnalytics = (writeKey: string): AnalyticsBrowser => {
  if (!publicAnalytics || publicAnalyticsWriteKey !== writeKey) {
    publicAnalytics = AnalyticsBrowser.load({ writeKey });
    publicAnalyticsWriteKey = writeKey;
  }

  return publicAnalytics;
};

const trackPublicOneTrustCookieMissingEvent: TrackWithWriteKey = (
  writeKey,
  eventName,
  properties,
) => getPublicAnalytics(writeKey).track(eventName, properties);

export function PublicOneTrustCookieMissingReporter({
  enabled,
  surface,
  writeKey,
  trackWithWriteKey = trackPublicOneTrustCookieMissingEvent,
}: Readonly<PublicOneTrustCookieMissingReporterProps>) {
  const track = useCallback(
    (
      eventName: "Error Reported",
      properties: OneTrustCookieMissingEventProperties,
    ) => {
      if (!writeKey) return Promise.resolve();

      return trackWithWriteKey(writeKey, eventName, properties);
    },
    [trackWithWriteKey, writeKey],
  );

  return (
    <OneTrustCookieMissingReporter
      enabled={enabled && Boolean(writeKey)}
      surface={surface}
      track={track}
    />
  );
}
